import { Box, keyframes, styled } from '@mui/material';

import { LOADING_LOOP_MS, REDUCED_MOTION } from './timeline';

const FRAME_WIDTH = 96;
const FRAME_HEIGHT = 105;
const PAGE_WIDTH = 68;
const PAGE_HEIGHT = 87;

/** The glow that trails the scan line, tall enough to read as light rather than a second rule. */
const GLOW_HEIGHT = 17;

/**
 * The three pages, back to front, as `[left, top]`.
 *
 * A fan rather than a stack: each page sits two pixels left and two pixels down from the one
 * behind it, which is what makes a batch legible at 96px without drawing three whole documents.
 */
const PAGE_OFFSETS = [
    [18, 5],
    [16, 7],
] as const;

const FRONT_PAGE_LEFT = 14;
const FRONT_PAGE_TOP = 9;

/** Where the scan line rests before it sets off, measured from the frame's top. */
const SCAN_LINE_TOP = 4;

/**
 * How far the scan line travels: from its resting position to the bottom edge of the page it is
 * reading. Derived rather than fixed, so the line cannot end up sweeping past the document or
 * stopping short of it when a page dimension changes.
 */
const SWEEP_DISTANCE = FRONT_PAGE_TOP + PAGE_HEIGHT - SCAN_LINE_TOP;

/** The front page's heading lines, as `[top, width]`. All start at the page's left margin. */
const HEADING_LINES = [
    [8, 26],
    [19, 32],
    [24, 23],
] as const;

/** Where the three columns of body text start. */
const BODY_COLUMNS = [8, 24, 48] as const;

/** Each body row, as `[top, ...the width of the line in each column]`. */
const BODY_ROWS = [
    [34, 12, 15, 12],
    [40, 10, 19, 12],
    [46, 10, 16, 12],
    [52, 10, 20, 12],
    [58, 10, 15, 12],
    [64, 10, 17, 12],
    [70, 10, 16, 12],
    [76, 10, 19, 12],
] as const;

/**
 * Down the page, hold, back up, hold — the holds are what keep the sweep from reading as a
 * ping-pong. The percentages are the design's own keyframe times against the shared loop.
 */
const sweep = keyframes`
    0%     { transform: translateY(0); }
    6.36%  { transform: translateY(0); }
    50%    { transform: translateY(${SWEEP_DISTANCE}px); }
    56.36% { transform: translateY(${SWEEP_DISTANCE}px); }
    100%   { transform: translateY(0); }
`;

/** Lit only on the way down, so the glow always trails the line rather than leading it. */
const glowOnDescent = keyframes`
    0%     { opacity: 0; }
    6.36%  { opacity: 0; }
    10%    { opacity: 1; }
    46.36% { opacity: 1; }
    50%    { opacity: 0; }
    100%   { opacity: 0; }
`;

/** The same trick for the return trip, which is why this one sits below the line. */
const glowOnAscent = keyframes`
    0%     { opacity: 0; }
    56.36% { opacity: 0; }
    60%    { opacity: 1; }
    96.36% { opacity: 1; }
    100%   { opacity: 0; }
`;

const Frame = styled(Box)({
    position: 'relative',
    width: FRAME_WIDTH,
    height: FRAME_HEIGHT,
    overflow: 'hidden',
    flexShrink: 0,
});

const Page = styled(Box)(({ theme }) => ({
    position: 'absolute',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
}));

const TextLine = styled(Box)(({ theme }) => ({
    position: 'absolute',
    height: 3,
    borderRadius: 1,
    backgroundColor: theme.palette.grey[100],
}));

const ScanLineGroup = styled(Box)({
    position: 'absolute',
    left: 0,
    top: SCAN_LINE_TOP,
    width: FRAME_WIDTH,
    height: 1,
    animation: `${sweep} ${LOADING_LOOP_MS}ms ease-in-out infinite`,
    // Hidden rather than parked, like the glows it carries. A scan line frozen at the top edge of
    // the frame reads as a stray rule across the page, not as a scanner holding still.
    [REDUCED_MOTION]: {
        animation: 'none',
        opacity: 0,
    },
});

const ScanLine = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    backgroundColor: theme.palette.purple[500],
}));

const Glow = styled(Box)({
    position: 'absolute',
    left: 0,
    width: FRAME_WIDTH,
    height: GLOW_HEIGHT,
    animationDuration: `${LOADING_LOOP_MS}ms`,
    animationTimingFunction: 'ease-out',
    animationIterationCount: 'infinite',
    [REDUCED_MOTION]: {
        animation: 'none',
        opacity: 0,
    },
});

/** Sits above the line, so it reads as the light the line has already passed through. */
const DescentGlow = styled(Glow)(({ theme }) => ({
    bottom: 0,
    background: `linear-gradient(to bottom, transparent, ${theme.palette.purple[200]})`,
    animationName: glowOnDescent,
}));

const AscentGlow = styled(Glow)(({ theme }) => ({
    top: 0,
    background: `linear-gradient(to bottom, ${theme.palette.purple[200]}, transparent)`,
    animationName: glowOnAscent,
}));

/**
 * A small stack of statements with a line sweeping over the top one.
 *
 * Decorative — it says "we are reading your files" and nothing a member has to act on, so it is
 * hidden from assistive tech and the progress line beside it carries the announcement.
 */
export default function ScanningDocument() {
    return (
        <Frame aria-hidden>
            {PAGE_OFFSETS.map(([left, top]) => (
                <Page key={`page-${left}`} style={{ left, top }} />
            ))}

            <Page style={{ left: FRONT_PAGE_LEFT, top: FRONT_PAGE_TOP }}>
                {HEADING_LINES.map(([top, width]) => (
                    <TextLine key={`heading-${top}`} style={{ left: BODY_COLUMNS[0], top, width }} />
                ))}
                {BODY_ROWS.map(([top, ...widths]) =>
                    widths.map((width, column) => (
                        <TextLine
                            key={`body-${top}-${BODY_COLUMNS[column]}`}
                            style={{ left: BODY_COLUMNS[column], top, width }}
                        />
                    ))
                )}
            </Page>

            <ScanLineGroup>
                <DescentGlow />
                <AscentGlow />
                <ScanLine />
            </ScanLineGroup>
        </Frame>
    );
}
